import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { List, CaretLeft, Check, Plus, MapTrifold, GridFour } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { queryKeys } from '../queries/keys';
import Sidebar from '../components/Sidebar';
import SearchBox from '../components/SearchBox';
import PlaceDetailModal from '../components/PlaceDetailModal';
import ItemDetailModal from '../components/ItemDetailModal';
import UploadModal from '../components/UploadModal';
import EmptyState from '../components/EmptyState';
import { CardGridSkeleton } from '../components/Skeleton';
import Toast from '../components/Toast';
import SelectionBar from '../components/SelectionBar';
import CollectionPickerModal from '../components/CollectionPickerModal';
import SearchSummary from '../components/SearchSummary';
import { cardAccessibleName } from '../utils/accessibleName';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useSearch } from '../hooks/useSearch';

// Leaflet + react-leaflet + react-leaflet-cluster add real bundle weight
// (~300KB gzipped, COL-50) — lazy-loaded so it's only fetched when a user
// actually switches to map view, not on every page load or grid-mode visit.
const PlacesMap = lazy(() => import('../components/PlacesMap'));

const PAGE_SIZE = 100;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
  { value: 'rating', label: 'Rating (High-Low)' },
  { value: 'sources', label: 'Most Sources' },
];
const MAX_PAGES = 20;

// Convert a single enrichment into the grouped place shape used by the page and modal
function enrichmentToPlaceGroup(enrichment, key) {
  return {
    key: key || enrichment.place_data?.google_place_id || enrichment.entity_name.toLowerCase().trim(),
    entity_name: enrichment.entity_name,
    entity_type: enrichment.entity_type,
    place_data: enrichment.place_data || {},
    enrichments: [enrichment],
    creator_contexts: enrichment.creator_context
      ? [{ text: enrichment.creator_context, item_headline: enrichment.item_headline, item_image: enrichment.item_image, item_id: enrichment.item_id }]
      : [],
    sources: [{ item_id: enrichment.item_id, item_headline: enrichment.item_headline, item_image: enrichment.item_image, enrichment_id: enrichment.enrichment_id }],
  };
}

export default function Places() {
  const { user, logout } = useAuth();
  useScrollRestoration();
  // Map mode is URL-addressable from day one (COL-41/50): `?view=map` so a
  // link or refresh lands back on the same mode, `replace: true` because
  // switching view isn't a navigation step worth its own back-stack entry.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'map' ? 'map' : 'grid';
  const setView = useCallback((next) => {
    setSearchParams((prev) => {
      if (next === 'grid') prev.delete('view');
      else prev.set('view', next);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedPlaceIndex, setSelectedPlaceIndex] = useState(-1);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBoxResetKey, setSearchBoxResetKey] = useState(0);
  const [sortBy, setSortBy] = useState('newest');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState(new Set());
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);

  const toastTimeoutRef = useRef(null);
  const showToast = useCallback((message, type = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Fetch geo counts for sidebar. Cache-first — instant on revisit.
  // enabled: !!user?.id is a second, query-level guard against ever firing
  // under the shared 'anon' key namespace (see queries/keys.js) — belt and
  // braces alongside ProtectedRoute never mounting this page pre-auth.
  const { data: geoCounts } = useQuery({
    queryKey: queryKeys.placeGeoCounts(user?.id),
    queryFn: () => api.getPlaceGeoCounts(),
    enabled: !!user?.id,
  });

  // Fetch all place enrichments (paginate through all results internally —
  // this page has always eagerly loaded everything rather than
  // infinite-scrolling, so that behavior is preserved as a single query
  // whose queryFn walks all cursor pages itself).
  const fetchAllPlaceEnrichments = useCallback(async () => {
    const allEnrichments = [];
    let cursor = null;
    let pages = 0;
    do {
      const data = await api.getEnrichments({
        status: 'complete',
        entityType: 'place',
        limit: PAGE_SIZE,
        ...(cursor ? { cursor } : {}),
      });
      allEnrichments.push(...(data.enrichments || []));
      cursor = data.next_cursor || null;
      pages++;
    } while (cursor && pages < MAX_PAGES);
    return allEnrichments;
  }, []);

  const { data: enrichmentsData, isLoading: loading, isError } = useQuery({
    queryKey: queryKeys.placeEnrichments(user?.id),
    queryFn: fetchAllPlaceEnrichments,
    enabled: !!user?.id,
  });
  const enrichments = enrichmentsData ?? [];

  useEffect(() => {
    if (isError) showToast('Failed to load places', 'error');
  }, [isError, showToast]);

  // Group enrichments by place (google_place_id or normalized entity_name)
  const groupedPlaces = useMemo(() => {
    const groups = new Map();

    for (const e of enrichments) {
      const key = e.place_data?.google_place_id
        || e.entity_name.toLowerCase().trim();

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          entity_name: e.entity_name,
          entity_type: e.entity_type,
          place_data: e.place_data || {},
          enrichments: [],
          creator_contexts: [],
          sources: [],
          latest_created_at: e.created_at || null,
        });
      }

      const group = groups.get(key);
      group.enrichments.push(e);

      // Track most recent enrichment date for sorting
      if (e.created_at && (!group.latest_created_at || e.created_at > group.latest_created_at)) {
        group.latest_created_at = e.created_at;
      }

      if (e.creator_context) {
        group.creator_contexts.push({
          text: e.creator_context,
          item_headline: e.item_headline,
          item_image: e.item_image,
          item_id: e.item_id,
        });
      }

      group.sources.push({
        item_id: e.item_id,
        item_headline: e.item_headline,
        item_image: e.item_image,
        enrichment_id: e.enrichment_id,
      });

      // Use the richest place_data available
      if (e.place_data && Object.keys(e.place_data).length > Object.keys(group.place_data).length) {
        group.place_data = e.place_data;
      }
    }

    return Array.from(groups.values());
  }, [enrichments]);

  // Sort places based on selected sort option
  const sortedGroupedPlaces = useMemo(() => {
    return [...groupedPlaces].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.latest_created_at || 0) - new Date(a.latest_created_at || 0);
        case 'oldest':
          return new Date(a.latest_created_at || 0) - new Date(b.latest_created_at || 0);
        case 'name-desc': {
          const nameA = (a.place_data?.display_name || a.entity_name || '').toLowerCase();
          const nameB = (b.place_data?.display_name || b.entity_name || '').toLowerCase();
          return nameB.localeCompare(nameA);
        }
        case 'rating': {
          const ratingA = a.place_data?.rating || 0;
          const ratingB = b.place_data?.rating || 0;
          return ratingB - ratingA;
        }
        case 'sources':
          return (b.sources?.length || 0) - (a.sources?.length || 0);
        default: { // 'name' — alphabetical by country, region, then name
          const countryA = (a.place_data?.country || '').toLowerCase();
          const countryB = (b.place_data?.country || '').toLowerCase();
          if (countryA !== countryB) return countryA.localeCompare(countryB);
          const regionA = (a.place_data?.region || '').toLowerCase();
          const regionB = (b.place_data?.region || '').toLowerCase();
          if (regionA !== regionB) return regionA.localeCompare(regionB);
          const nameA = (a.place_data?.display_name || a.entity_name || '').toLowerCase();
          const nameB = (b.place_data?.display_name || b.entity_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        }
      }
    });
  }, [groupedPlaces, sortBy]);

  // Filter by selected country/region
  const filteredPlaces = useMemo(() => {
    if (selectedCountry === 'all') return sortedGroupedPlaces;
    return sortedGroupedPlaces.filter(p => {
      if (p.place_data?.country !== selectedCountry) return false;
      if (selectedRegion && p.place_data?.region !== selectedRegion) return false;
      return true;
    });
  }, [sortedGroupedPlaces, selectedCountry, selectedRegion]);

  // Search core (mutation + searching flag + isSubmit -> includeAnswer
  // mapping) is shared with Gallery/Entities via useSearch (COL-138).
  // transformResults filters the raw response down to place entities only —
  // Places-specific, unlike Gallery/Entities which use the response as-is.
  const { searchResults, setSearchResults, searching, search } = useSearch({
    scope: 'entities',
    transformResults: (results) => ({
      ...results,
      entities: (results.entities || []).filter(e => e.entity_type === 'place'),
    }),
    onError: () => showToast('Search failed', 'error'),
  });

  const handleSearch = useCallback((query, opts = {}) => {
    setSearchQuery(query.trim() ? query : '');
    search(query, opts);
  }, [search]);

  // Display items: search results or grouped places
  const displayPlaces = useMemo(() => {
    if (searchResults) {
      return (searchResults.entities || []).map(e => enrichmentToPlaceGroup(e, e.enrichment_id));
    }
    return filteredPlaces;
  }, [searchResults, filteredPlaces]);

  const handlePlaceClick = useCallback((place, index) => {
    setSelectedPlace(place);
    if (index !== undefined) setSelectedPlaceIndex(index);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedPlace(null);
    setSelectedPlaceIndex(-1);
  }, []);

  // Open item detail modal (for related items in place modal)
  const handleOpenItemModal = useCallback(async (itemId) => {
    try {
      const item = await api.getItem(itemId, { includeRawResponse: true });
      setSelectedItemForModal(item);
    } catch (err) {
      showToast('Failed to load item', 'error');
    }
  }, [showToast]);

  const handleCloseItemModal = useCallback(() => {
    setSelectedItemForModal(null);
  }, []);

  // Open a place modal from a sibling entity
  const handleOpenPlaceFromSibling = useCallback((enrichment) => {
    setSelectedPlace(enrichmentToPlaceGroup(enrichment));
    setSelectedPlaceIndex(-1);
  }, []);

  const handlePrevPlace = useCallback(() => {
    if (selectedPlaceIndex > 0) {
      setSelectedPlace(displayPlaces[selectedPlaceIndex - 1]);
      setSelectedPlaceIndex(selectedPlaceIndex - 1);
    }
  }, [selectedPlaceIndex, displayPlaces]);

  const handleNextPlace = useCallback(() => {
    if (selectedPlaceIndex < displayPlaces.length - 1) {
      setSelectedPlace(displayPlaces[selectedPlaceIndex + 1]);
      setSelectedPlaceIndex(selectedPlaceIndex + 1);
    }
  }, [selectedPlaceIndex, displayPlaces]);

  // Build sidebar categories from geo counts
  const categories = useMemo(() => {
    if (!geoCounts) {
      return [{ id: 'all', name: 'All Places', count: 0 }];
    }

    const countryCats = geoCounts.countries
      .map(c => ({
        id: c.name,
        name: c.name,
        count: c.count,
        subcategories: (c.regions || [])
          .map(r => ({
            id: r.name,
            name: r.name,
            count: r.count,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [
      { id: 'all', name: 'All Places', count: geoCounts.total },
      ...countryCats,
    ];
  }, [geoCounts]);

  return (
    <div className="app-layout places-page">
      <Sidebar
        categories={categories}
        selectedCategory={selectedCountry}
        selectedSubcategory={selectedRegion}
        onSelectCategory={(id, subId) => {
          setSelectedCountry(id);
          setSelectedRegion(subId || null);
          setSearchResults(null);
          setSearchQuery('');
          setSearchBoxResetKey(k => k + 1);
        }}
        user={user}
        onLogout={logout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <List size={20} />
            </button>
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              <CaretLeft size={16} />
              Items
            </Link>
            <h1 className="page-title">Places</h1>
            {searchResults && (
              <span style={{
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                marginLeft: 'var(--space-md)',
              }}>
                {displayPlaces.length} results
              </span>
            )}
          </div>

          <div className="header-actions">
            <SearchBox onSearch={handleSearch} placeholder="Search places..." key={searchBoxResetKey} />

            {!searchResults && (
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => {
                const next = view === 'map' ? 'grid' : 'map';
                // Select mode's checkbox UI doesn't apply to pin-based
                // interaction — leaving it on would strand the SelectionBar
                // floating over the map with no way to add to it.
                if (next === 'map' && isSelectMode) {
                  setIsSelectMode(false);
                  setSelectedPlaceIds(new Set());
                }
                setView(next);
              }}
              aria-label={view === 'map' ? 'Switch to grid view' : 'Switch to map view'}
              aria-pressed={view === 'map'}
            >
              {view === 'map' ? <GridFour size={20} /> : <MapTrifold size={20} />}
            </button>

            {view === 'grid' && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  if (isSelectMode) { setIsSelectMode(false); setSelectedPlaceIds(new Set()); }
                  else setIsSelectMode(true);
                }}
              >
                {isSelectMode ? 'Cancel' : 'Select'}
              </button>
            )}

            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <PlusIcon />
              <span>Upload</span>
            </button>
          </div>
        </header>

        {/* AI Answer (when searching) */}
        <SearchSummary
          answer={searchResults?.answer}
          onItemClick={(item) => handleOpenItemModal(item.item_id)}
        />

        <div className="content-body">
          {loading || searching ? (
            <CardGridSkeleton />
          ) : displayPlaces.length === 0 ? (
            <EmptyState
              title={searchQuery ? 'No matching places' : 'No places found'}
              description={searchQuery
                ? 'Try different search terms or clear the search'
                : selectedCountry !== 'all'
                  ? 'Try selecting a different region'
                  : 'Places will appear here after items are analyzed and enriched'}
            />
          ) : view === 'map' ? (
            <Suspense fallback={<CardGridSkeleton />}>
              <PlacesMap places={displayPlaces} onPlaceClick={handlePlaceClick} />
            </Suspense>
          ) : (
            <motion.div
              className="items-grid"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.03 } } }}
            >
              <AnimatePresence mode="sync">
                {displayPlaces.map((place, index) => {
                  const placeEnrichmentId = place.enrichments?.[0]?.enrichment_id || place.key;
                  return (
                    <div key={place.key} style={{ position: 'relative' }}>
                      {isSelectMode && (
                        <div
                          className={`collection-item-checkbox ${selectedPlaceIds.has(placeEnrichmentId) ? 'checked' : ''}`}
                          style={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}
                        >
                          {selectedPlaceIds.has(placeEnrichmentId) && (
                            <Check size={14} />
                          )}
                        </div>
                      )}
                      <PlaceCard
                        place={place}
                        index={index}
                        onClick={() => {
                          if (isSelectMode) {
                            setSelectedPlaceIds(prev => {
                              const next = new Set(prev);
                              if (next.has(placeEnrichmentId)) next.delete(placeEnrichmentId);
                              else next.add(placeEnrichmentId);
                              return next;
                            });
                          } else {
                            handlePlaceClick(place, index);
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Bottom spacer */}
          {displayPlaces.length > 0 && (
            <div style={{ minHeight: '60px' }} />
          )}
        </div>
      </main>

      <AnimatePresence>
        {selectedPlace && (
          <PlaceDetailModal
            place={selectedPlace}
            onClose={handleCloseModal}
            onPrev={handlePrevPlace}
            onNext={handleNextPlace}
            hasPrev={selectedPlaceIndex > 0}
            hasNext={selectedPlaceIndex < displayPlaces.length - 1}
            itemIndex={selectedPlaceIndex >= 0 ? selectedPlaceIndex : undefined}
            totalItems={selectedPlaceIndex >= 0 ? displayPlaces.length : undefined}
            onOpenItem={handleOpenItemModal}
            onOpenPlace={handleOpenPlaceFromSibling}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedItemForModal && (
          <ItemDetailModal
            item={selectedItemForModal}
            onClose={handleCloseItemModal}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpload && (
          <UploadModal
            onClose={() => setShowUpload(false)}
            onUpload={(newItem) => {
              setShowUpload(false);
              showToast('Screenshot uploaded! It will appear here after processing.', 'success');
            }}
          />
        )}
      </AnimatePresence>

      <Toast toast={toast} />

      <AnimatePresence>
        {isSelectMode && selectedPlaceIds.size > 0 && (
          <SelectionBar
            count={selectedPlaceIds.size}
            onAddToCollection={() => setShowCollectionPicker(true)}
            onCancel={() => { setIsSelectMode(false); setSelectedPlaceIds(new Set()); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCollectionPicker && (
          <CollectionPickerModal
            items={Array.from(selectedPlaceIds).map(id => ({ item_id: id, item_type: 'enrichment' }))}
            onClose={() => setShowCollectionPicker(false)}
            onSuccess={(msg) => {
              showToast(msg, 'success');
              setIsSelectMode(false);
              setSelectedPlaceIds(new Set());
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PlusIcon() {
  return <Plus size={18} />;
}

function PlaceCard({ place, index, onClick }) {
  const { entity_name, place_data, sources } = place;
  const displayName = place_data?.display_name || entity_name;
  const firstSource = sources[0];
  // COL-103 (owner decision 2026-08-17): Google Places photos are no longer
  // requested, stored or rendered. The item screenshot is the only image —
  // unconditionally, not as a fallback behind a failed photo lookup.
  const image = firstSource?.item_image;

  // Accessible name states what the card IS (place name + type), not "card"
  // or "button".
  const accessibleName = cardAccessibleName(displayName, place_data?.primary_type, { fallbackType: 'place' });

  return (
    <motion.button
      type="button"
      className="item-card card-button-reset"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.03 }}
      onClick={onClick}
      aria-label={accessibleName}
    >
      <div className="item-card-image">
        {image && <img src={image} alt={displayName} loading="lazy" />}
        <span className="item-card-badge">{place_data?.primary_type || 'place'}</span>
      </div>

      <div className="item-card-content">
        <div className="item-card-title">{displayName}</div>
        {(place_data?.city || place_data?.region) && (
          <div className="item-card-source">
            {[place_data.city, place_data.region].filter(Boolean).join(', ')}
          </div>
        )}
        {place_data?.rating && (
          <div className="place-card-rating">
            <span className="place-card-star">★</span> {place_data.rating}
          </div>
        )}
      </div>

      {sources.length > 1 && (
        <div className="item-card-footer">
          <span className="item-card-date">From {sources.length} items</span>
        </div>
      )}
    </motion.button>
  );
}
